// api/apiService.js
import axios from "axios";
import getHeaders from "./getheaders";

const API_BASE_URL = process.env.REACT_APP_HOST_URL || "http://localhost:8080";

const createAPIInstance = () => {
  const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
    validateStatus: (status) => {
      return status >= 200 && status < 500;
    },
  });

  api.interceptors.request.use(
    (config) => {
      config.metadata = { startTime: new Date() };

      // Determine if the request contains FormData
      const isFormData = config.data instanceof FormData;

      // Get headers based on request type
      const headers = getHeaders(isFormData);

      // Merge headers with config
      config.headers = {
        ...headers,
        ...config.headers,
      };

      if (process.env.NODE_ENV === "development") {
        console.log(
          `[API Request] ${config.method.toUpperCase()} ${config.url}`,
          {
            data: config.data,
            headers: config.headers,
          }
        );
      }
      return config;
    },
    (error) => {
      console.error("Request Interceptor Error:", error);
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      if (response.status === 201) {
        response.statusText = "Created";
      } else if (response.status >= 200 && response.status < 400) {
        response.statusText = "OK";
      } else if (response.status >= 400 && response.status < 500) {
        response.statusText = "Bad Request";
      }

      const duration = new Date() - response.config.metadata.startTime;

      if (process.env.NODE_ENV === "development") {
        console.log(
          `[API Response] ${response.config.method.toUpperCase()} ${
            response.config.url
          }`,
          {
            status: response.status,
            data: response.data,
            duration: `${duration}ms`,
          }
        );
      }

      if (response.status >= 400 && response.status < 500) {
        return {
          ...response,
          isError: true,
          error: {
            status: response.status,
            message: response.data.message || "An error occurred",
          },
        };
      }
      return response;
    },
    async (error) => {
      if (error.response && error.response.status >= 500) {
        const errorMessage =
          error.response.data?.message || "A server error occurred";
        console.error("Server Error:", {
          status: error.response.status,
          message: errorMessage,
        });
        return Promise.reject({
          ...error,
          message: errorMessage,
        });
      }

      if (!error.response) {
        console.error("Network Error:", error.message);
        return Promise.reject({
          ...error,
          message: "Network error. Please check your connection.",
        });
      }
      return Promise.reject(error);
    }
  );

  return api;
};

const api = createAPIInstance();

export const apiService = {
  get: (url, config = {}) => api.get(url, config),

  post: (url, data, config = {}) => {
    const isFormData = data instanceof FormData;
    if (isFormData) {
      config = {
        ...config,
        headers: {
          ...getHeaders(true),
          ...config.headers,
        },
      };
    }
    return api.post(url, data, config);
  },

  put: (url, data, config = {}) => {
    const isFormData = data instanceof FormData;
    if (isFormData) {
      config = {
        ...config,
        headers: {
          ...getHeaders(true),
          ...config.headers,
        },
      };
    }
    return api.put(url, data, config);
  },

  patch: (url, data, config = {}) => {
    const isFormData = data instanceof FormData;
    if (isFormData) {
      config = {
        ...config,
        headers: {
          ...getHeaders(true),
          ...config.headers,
        },
      };
    }
    return api.patch(url, data, config);
  },

  delete: (url, config = {}) => api.delete(url, config),
};

export default apiService;
